import { z } from 'zod';
import { Money } from './money';

export const TodayReport = z.object({
  revenue: Money,
  checks: z.number().int(),
  avgCheck: Money,
  cancelled: z.number().int(),
  /** людей с открытой явкой прямо сейчас */
  onShift: z.number().int(),
  openShifts: z.number().int(),
  byMethod: z.array(z.object({
    method: z.enum(['CASH', 'CARD', 'KASPI_QR']),
    amount: Money,
  })),
  /** выручка по часам сегодняшнего дня — спарклайн дашборда */
  byHour: z.array(z.object({ hour: z.number().int(), revenue: Money })).length(24),
});
export type TodayReport = z.infer<typeof TodayReport>;

export const DayCheckRow = z.object({
  orderId: z.string(),
  number: z.number().int(),
  closedAt: z.string().datetime(),
  cashierName: z.string(),
  total: Money,
  status: z.enum(['CLOSED', 'CANCELLED']),
  /** для возвратов: причина и кто одобрил PIN-ом */
  cancelReason: z.string().nullable().optional(),
  cancelApprover: z.string().nullable().optional(),
  methods: z.array(z.enum(['CASH', 'CARD', 'KASPI_QR'])),
});
export const DayChecks = z.object({
  rows: z.array(DayCheckRow),
  revenue: Money,
  checks: z.number().int(),
  /** «на чай картой» за период — деньги официантов, не выручка */
  tips: Money.optional(),
});
export type DayChecks = z.infer<typeof DayChecks>;

export const AbcRow = z.object({
  name: z.string(),
  qty: z.number(),
  revenue: Money,
  share: z.number(),      // доля выручки, 0..1
  grade: z.enum(['A', 'B', 'C']),
});
/** Меню-инженерия: 2×2 по частоте и марже за единицу. */
export const EngRow = z.object({ name: z.string(), qty: z.number(), marginPerUnit: Money });
export const AbcReport = z.object({
  rows: z.array(AbcRow),
  revenue: Money,
  engineering: z.object({
    stars: z.array(EngRow),   // популярно и маржинально — беречь
    horses: z.array(EngRow),  // популярно, маржа ниже — поднять цену/себестоимость вниз
    puzzles: z.array(EngRow), // маржинально, но редко — продвигать
    dogs: z.array(EngRow),    // ни того ни другого — кандидат на вылет
    noCost: z.number().int(), // позиций без себестоимости — вне анализа
  }),
});
export type AbcReport = z.infer<typeof AbcReport>;

export const PromoReportRow = z.object({
  name: z.string(),
  count: z.number().int(), // в скольких чеках сработала
  amount: Money,           // сколько подарили гостям
});
export const PromoReport = z.object({ rows: z.array(PromoReportRow) });
export type PromoReport = z.infer<typeof PromoReport>;

export const HoursRow = z.object({
  hour: z.number().int().min(0).max(23),
  checks: z.number().int(),
  revenue: Money,
});
export const WeekdayRow = z.object({
  dow: z.number().int().min(0).max(6), // 0 — понедельник
  revenue: Money,
  checks: z.number().int(),
  days: z.number().int(),
  avgPerDay: Money,
});

export const HoursReport = z.object({
  byWeekday: z.array(WeekdayRow).length(7),
  rows: z.array(HoursRow).length(24),
  peakHour: z.number().int().nullable(),
});
export type HoursReport = z.infer<typeof HoursReport>;

/** Фудкост: сколько стоит то, что мы продаём.
 *  Себестоимость — по текущей средневзвешенной цене закупок. */
export const FoodcostRow = z.object({
  productId: z.string(),
  name: z.string(),
  qty: z.number(),
  revenue: Money,
  unitCost: Money.nullable(),
  cost: Money.nullable(),
  margin: Money.nullable(),
  /** доля себестоимости в выручке позиции, 0..1 */
  pct: z.number().nullable(),
});
export const FoodcostReport = z.object({
  rows: z.array(FoodcostRow),
  revenue: Money,
  /** выручка позиций с известной себестоимостью */
  knownRevenue: Money,
  cost: Money,
  margin: Money,
});
export type FoodcostReport = z.infer<typeof FoodcostReport>;

/** Реестр продаж для бухгалтерии/1С: строка на позицию чека.
 *  Возвраты (проведённые в ОФД) идут с минусом. */
export const SalesRegisterRow = z.object({
  at: z.string().datetime(),
  number: z.number().int(),
  kind: z.enum(['SALE', 'REFUND']),
  fiscalNumber: z.string().nullable(),
  name: z.string(),
  qty: z.number(),
  price: Money,
  sum: Money,
  /** себестоимость строки по снапшоту продажи; null — снапшота нет */
  cost: Money.nullable(),
  methods: z.array(z.enum(['CASH', 'CARD', 'KASPI_QR'])),
});
export const SalesRegister = z.object({
  rows: z.array(SalesRegisterRow),
  totalSum: Money,
  /** сумма себестоимости по строкам, где она известна */
  totalCost: Money,
});
export type SalesRegister = z.infer<typeof SalesRegister>;

/** «Закрытие дня»: один печатный лист — итог, методы, топ, смены. */
export const DayCloseShift = z.object({
  cashier: z.string(),
  openedAt: z.string().datetime(),
  closedAt: z.string().datetime().nullable(),
  checks: z.number().int(),
  revenue: Money,
  cashSales: Money,
  openingCash: Money,
  expectedCash: Money,
  payIn: Money,
  payOut: Money,
});
export const DayCloseReport = z.object({
  date: z.string(),
  place: z.string(),
  locationName: z.string().nullable(),
  revenue: Money,
  checks: z.number().int(),
  avgCheck: Money,
  cancelledCount: z.number().int(),
  cancelledSum: Money,
  byMethod: z.array(z.object({
    method: z.enum(['CASH', 'CARD', 'KASPI_QR']),
    amount: Money,
  })),
  top: z.array(z.object({ name: z.string(), qty: z.number(), sum: Money })).max(5),
  shifts: z.array(DayCloseShift),
});
export type DayCloseReport = z.infer<typeof DayCloseReport>;

/** Отчёт по сотрудникам: кто набирал столы, кто закрывал чеки. */
export const StaffSalesRow = z.object({
  name: z.string(),
  openedChecks: z.number().int(),
  openedSum: Money,
  closedChecks: z.number().int(),
  closedSum: Money,
});
export const StaffSalesReport = z.object({ rows: z.array(StaffSalesRow) });
export type StaffSalesReport = z.infer<typeof StaffSalesReport>;

/** Кухня прямо сейчас — для дашборда офиса. */
export const KitchenNowRow = z.object({
  number: z.number().int(),
  table: z.string().nullable(),
  items: z.number().int(),
  minutes: z.number().int(),
  status: z.enum(['NEW', 'COOKING', 'READY']),
});
export const KitchenNow = z.object({ rows: z.array(KitchenNowRow) });
export type KitchenNow = z.infer<typeof KitchenNow>;

/** Расход учётных позиций за период: «сколько мяса ушло». */
export const ConsumptionRow = z.object({
  name: z.string(),
  unit: z.enum(['PIECE', 'KG', 'L', 'PORTION']),
  used: z.number(),
  avgCost: Money.nullable(),
  costValue: Money.nullable(),
});
export const ConsumptionReport = z.object({
  rows: z.array(ConsumptionRow),
  totalValue: Money,
});
export type ConsumptionReport = z.infer<typeof ConsumptionReport>;

/** Внесения/изъятия за период: депозиты, размен, инкассация. */
export const CashOpRow = z.object({
  at: z.string().datetime(),
  kind: z.enum(['PAY_IN', 'PAY_OUT']),
  amount: Money,
  comment: z.string().nullable(),
  byName: z.string(),
});
export const CashOpsReport = z.object({
  rows: z.array(CashOpRow),
  totalIn: Money,
  totalOut: Money,
});
export type CashOpsReport = z.infer<typeof CashOpsReport>;

/** Отзывы за период: оценки гостей с чеков. */
export const FeedbackRow = z.object({
  at: z.string().datetime(),
  rating: z.number().int(),
  comment: z.string().nullable(),
  orderNumber: z.number().int().nullable(),
});
export const FeedbackReport = z.object({
  rows: z.array(FeedbackRow),
  avg: z.number().nullable(),
  count: z.number().int(),
});
export type FeedbackReport = z.infer<typeof FeedbackReport>;

/** Вызовы со столов: скорость реакции зала. */
export const CallRow = z.object({
  at: z.string().datetime(),
  tableName: z.string(),
  kind: z.enum(['WAITER', 'BILL']),
  reactionSec: z.number().int().nullable(),
});
export const CallsReport = z.object({
  rows: z.array(CallRow),
  avgSec: z.number().int().nullable(),
  count: z.number().int(),
});
export type CallsReport = z.infer<typeof CallsReport>;

/** Скорость кухни: от оплаты до READY, по часам. */
export const KitchenSpeed = z.object({
  rows: z.array(z.object({
    hour: z.number().int(),
    tickets: z.number().int(),
    avgSec: z.number().int(),
  })),
  avgSec: z.number().int().nullable(),
  tickets: z.number().int(),
});
export type KitchenSpeed = z.infer<typeof KitchenSpeed>;

/** Депозиты броней: внесено, использовано в чеках, сгорело. */
export const DepositsReport = z.object({
  rows: z.array(z.object({
    at: z.string().datetime(),
    guestName: z.string(),
    tableName: z.string(),
    deposit: Money,
    status: z.enum(['PLANNED', 'SEATED', 'CANCELLED', 'NO_SHOW']),
    used: z.boolean(),
  })),
  total: Money,
  used: Money,
  burned: Money,
});
export type DepositsReport = z.infer<typeof DepositsReport>;

/** Средний чек по режимам: зал, с собой, доставка. */
export const ModesReport = z.object({
  rows: z.array(z.object({
    mode: z.enum(['DINE_IN', 'TAKEAWAY', 'DELIVERY']),
    checks: z.number().int(),
    revenue: Money,
  })),
});
export type ModesReport = z.infer<typeof ModesReport>;

/** Сеть на одном экране: точки бок о бок. */
export const ByLocationReport = z.object({
  rows: z.array(z.object({
    locationId: z.string(),
    name: z.string(),
    checks: z.number().int(),
    revenue: Money,
  })),
});
export type ByLocationReport = z.infer<typeof ByLocationReport>;

/** Прогноз до конца месяца: факт + среднее последних дней. */
export const ForecastReport = z.object({
  monthSoFar: Money,
  avgDay: Money,
  daysLeft: z.number().int(),
  projected: Money,
});
export type ForecastReport = z.infer<typeof ForecastReport>;

/** Средний чек по неделям — тренд, не таблица. */
export const AvgWeeksReport = z.object({
  rows: z.array(z.object({
    weekStart: z.string(),
    checks: z.number().int(),
    avg: Money,
  })),
});
export type AvgWeeksReport = z.infer<typeof AvgWeeksReport>;

/** «Часто берут вместе» — счётные пары для владельца. */
export const PairsReport = z.object({
  rows: z.array(z.object({
    a: z.string(),
    b: z.string(),
    count: z.number().int(),
  })),
});
export type PairsReport = z.infer<typeof PairsReport>;

/** Теплокарта 7×24: где неделя дышит. */
export const HeatReport = z.object({
  rows: z.array(z.object({
    wd: z.number().int().min(0).max(6),
    hour: z.number().int().min(0).max(23),
    revenue: Money,
  })),
});
export type HeatReport = z.infer<typeof HeatReport>;
